import React, { useEffect, useRef, useState } from "react"
import { scene, engine, xr } from "../3D/setup"
import { TextBlock3D } from "./3DUIElements/TextBlock3D"
import { ImageBlock } from "./3DUIElements/ImageBlock"
import { DialPicker } from "./3DUIElements/DialPicker"
import { RangeSlider } from "./3DUIElements/RangeSlider"
import { ParentPlane } from "./3DUIElements/ParentPlane"
import { DropdownSelectionBox } from "./3DUIElements/DropdownSelectionBox"
import { ColourPicker } from "./3DUIElements/ColourPicker"
import { Button } from "./3DUIElements/Button"
import { RadioButton } from "./3DUIElements/RadioButton"

interface SpatialiseProps {
	children: React.ReactNode
	position: [number, number, number]
	grabbable?: boolean
	visibility?: boolean
	colour?: string
	accentColour1?: string
	accentColour2?: string
	accentColour3?: string
	accentColour4?: string
	text1?: string
	size?: [number, number]
	orientation?: "horizontal" | "vertical"
	onAngleChanged?: (newAngle: number) => void
	dropdownOptions?: { label: string; value: any }[]
	onSelectionChanged?: (newValue: any) => void // Callback for when selection changes
	isParent?: boolean // Indicates if this instance should create a parent plane
	parentID?: string // Optional ID of the parent 3D plane
	id?: string // Optional ID of the parent 3D plane
	scalerSize?: number
	buttonLabel?: string
	onButtonClick?: () => void
	buttonColor?: string
}

export function Spatialise({
	children,
	position,
	colour = "black",
	accentColour1 = "white",
	accentColour2 = "white",
	accentColour3 = "white",
	accentColour4 = "white",
	text1 = "white",
	grabbable = false,
	visibility = false,
	size = [1, 1],
	scalerSize = 1,
	orientation = "horizontal",
	onAngleChanged,
	dropdownOptions,
	onSelectionChanged,
	buttonLabel,
	onButtonClick,
	isParent = false,
	parentID,
}: SpatialiseProps) {
	const [sceneCreated, setSceneCreated] = useState(false)
	const [parentReady, setParentReady] = useState(false) // New state to track parent readiness
	window.addEventListener("sceneLoadedEvent", function (e) {
		setSceneCreated(true)
		console.log("Scene creation complete:")
		// Additional actions after scene creation can be placed here.
	})
	useEffect(() => {
		if (!sceneCreated) {
			// If isParent, create a parent plane and use its ID for child blocks
			let localParentID = parentID
			// Process children, potentially with a parentID
			processChild(children, localParentID)
		}
	}, [
		children,
		position,
		colour,
		size,
		grabbable,
		orientation,
		isParent,
		parentID,
		sceneCreated,
	])

	const processChild = (child: React.ReactNode, parentID?: string) => {
		if (React.isValidElement(child)) {
			const props = child.props
			// Extend this with actual properties relevant for each block type
			const content = props.children || props.text || ""
			const imageUrl = props.imageUrl || ""
			const btnImageUrl = props.btnImageUrl || ""
			const btnImageUrl1 = props.btnImageUrl1 || ""
			const fontSize = props.fontSize || ""
			const fontWeight = props.fontWeight || "normal"
			const parent = props.parent || ""
			const slider = props.slider || false
			const colourPicker = props.colourPicker || false
			const dialPicker = props.dialPicker || false
			const dropdown = props.defaultKey || null
			// New handling for button
			const onClick = props.buttonLabel
			const onCheckChanged = props.onCheckChanged
			var preview = false

			// Example: Creating an image block as a child of the parent plane
			if (process.env.REACT_APP_VR_PREVIEW_FLAG == "true") {
				visibility = true
				preview = true
			}
			if (parent) {
				//localParentID = `parent_${Math.random().toString(36).substr(2, 9)}` // Generate a unique ID for the parent
				ParentPlane.create(
					scene!,
					parent as string,
					position,
					size,
					colour,
					accentColour1,
					grabbable,
					visibility,
					parentID
				)
			} else if (imageUrl) {
				ImageBlock.create(
					scene!,
					"UI_image_" + Math.random().toString(36).substr(2, 9),
					imageUrl,
					position,
					size,
					grabbable,
					visibility,
					parentID
				)
			} else if (dropdown != null) {
				DropdownSelectionBox.create(
					scene!,
					"UI_dropdown_" + Math.random().toString(36).substr(2, 9), // Unique ID
					props.allValues,
					position,
					size,
					props.onChangeSpatial,
					grabbable,
					visibility,
					colour,
					accentColour1,
					accentColour1,
					accentColour2,
					accentColour3,
					parentID
				)
			} else if (colourPicker) {
				// Handle as a color picker
				//const { onColorChanged } = props.onChange
				ColourPicker.create(
					scene!,
					engine!,
					"UI_colourPicker_" + Math.random().toString(36).substr(2, 9),
					position,
					scalerSize,
					props.onColorChanged,
					grabbable,
					visibility,
					parentID
				)
			} else if (dialPicker) {
				const { onAngleChanged } = props
				// Handle as a dial picker
				DialPicker.create(
					scene!,
					xr,
					"UI_dialPicker_" + Math.random().toString(36).substr(2, 9), // Unique ID for each dial picker
					position,
					0.2, // Assuming a default size, adjust as needed
					colour,
					accentColour1,
					onAngleChanged!,
					grabbable,
					visibility,
					parentID
				)
			} else if (slider) {
				// Handle as a 3D range slider
				RangeSlider.create(
					scene!,
					"UI_slider_" + Math.random().toString(36).substr(2, 9), // Unique ID for each slider
					position,
					size,
					props.min,
					props.max,
					props.value,
					props.onChange,
					grabbable,
					visibility,
					orientation,
					colour,
					accentColour1,
					parentID
				)
			} else if (onClick) {
				Button.create(
					scene!,
					"UI_button_" + Math.random().toString(36).substr(2, 9), // Generating a unique ID for the button
					position,
					size,
					onClick,
					btnImageUrl,
					btnImageUrl1,
					onButtonClick as () => void,
					colour,
					accentColour1,
					grabbable,
					visibility,
					parentID
				)
			} else if (onCheckChanged) {
				RadioButton.create(
					scene!,
					"UI_button_" + Math.random().toString(36).substr(2, 9), // Generating a unique ID for the button
					position,
					size,
					props.imageURL,
					props.group,
					colour,
					onCheckChanged,
					props.isChecked,
					visibility,
					grabbable,
					parentID
				)
			} else if (typeof content === "string") {
				// Example usage

				TextBlock3D.create(
					scene!, // Assuming `spatialUIElements` is the class where you previously had `createTextBlock3D`
					"UI_text_" + Math.random().toString(36).substr(2, 9), // Generating a unique ID for the button
					content,
					position,
					fontSize,
					fontWeight,
					colour,
					grabbable,
					visibility,
					size,
					parentID
				)
			}
		}
	}

	return <>{children}</>
}
